import React, { useState, useEffect, useContext } from 'react';
import { useParams } from 'react-router-dom';
import { Typography, Grid, Container, CircularProgress} from '@mui/material';
import EventGallery from '../components/events/EventGallery';
import { useAuth } from '../contexts/AuthContext';
import { useUser } from '../contexts/UserContext';
import { useCart } from '../contexts/CartContext';
import CartContext from '../contexts/CartContext';
import addToCartItem from '../hooks/cart/addToCartItem';
import addToWaitlist from '../hooks/cart/addToWaitlist';
import removeItemFromCart from '../hooks/cart/removeItemFromCart';
import { getClientId, getEventStatus } from '../utils';
import axios from 'axios';
import CustomSnackbar from '../components/elements/CustomSnackbar';
import ConfirmDialog from '../components/elements/ConfirmDialog';
import { EventDetail, CartItemType, ExpandedAccordionsType } from '../types';
import EventKeyInformation from '../components/events/EventKeyInformation';
import EventPricingTiersBoxes from '../components/events/EventPricingTiersBoxes';
import EventStatusPaper from '../components/events/EventStatusPaper';
import EventDescription from '../components/events/EventDescription';
import EventHeader from '../components/events/EventHeader';
import { KEY_EVENT_STATUS_ACTIVE } from '../constants';

const EventPage: React.FC = () => {
    const { id } = useParams<{ id: string }>();
    const [event, setEvent] = useState<EventDetail | null>(null);
    const [eventDetails, setEventDetails] = useState<EventDetail | null>(null);
    const [loading, setLoading] = useState<boolean>(true);
    const { user } = useUser();
    const { incrementCartItemsTotal, fetchCart } = useCart();
    const { cartItems } = useContext(CartContext) as unknown as { cartItems: CartItemType };
    const events_api_url = import.meta.env.VITE_EVENTS_API_URL;
    const client_id = getClientId();
    const [currentPricingId, setCurrentPricingId] = useState<number | null>(null);
    const [currentSelectedDays, setCurrentSelectedDays] = useState<string[] | null>(null);
    const { handleLoginClick } = useAuth();
    const [openDialog, setOpenDialog] = useState<boolean>(false);
    const [openWaitlistDialog, setOpenWaitlistDialog] = useState<{ id: number | null; open: boolean }>({ id: null, open: false });
    const [snackbar, setSnackbar] = useState<{ open: boolean; message: string; severity: 'success' | 'error' | 'info' | 'warning' }>({ open: false, message: '', severity: 'success' });
    const [expandedAccordions, setExpandedAccordions] = useState<ExpandedAccordionsType>({});

    useEffect(() => {
        const fetchEventDetails = async () => {
            setLoading(true);
            try {
                const response = await axios.get(`${events_api_url}/events/${id}`, {
                    params: { client_id, isPublic: 1 }
                });
                setEvent(response.data.data);
                setEventDetails(response.data.data);
            } catch (error) {
                console.error('Error fetching event details:', error);
                setSnackbar({ open: true, message: 'Failed to fetch event details.', severity: 'error' });
            } finally {
                setLoading(false);
            }
        };

        fetchEventDetails();
    }, [id]);

    const handleShare = () => {
        navigator.clipboard.writeText(window.location.href);
        setSnackbar({ open: true, message: 'Link copied to clipboard!', severity: 'success' });
    };

    const handleAddToCartClick = async (pricingId: number, isSelectScheduleAllowed: boolean, maxScheduleDays: number, selectedDaysArray: string[], handleAccordion: boolean) => {
        if (!user) {
            handleLoginClick();
            return;
        }

        if (isSelectScheduleAllowed) {
            if (handleAccordion) {
                setExpandedAccordions((prev) => ({
                    ...prev,
                    [pricingId]: !prev[pricingId],
                }));
            } else if (selectedDaysArray && selectedDaysArray.length <= maxScheduleDays) {
                if (selectedDaysArray.length < maxScheduleDays) {
                    handleOpenDialog(pricingId, selectedDaysArray);
                } else {
                    const arrayResponse = await addToCartItem(pricingId, user.selectedChild!.id, selectedDaysArray);
                    setSnackbar({ open: true, message: arrayResponse.message, severity: arrayResponse.success ? 'success' : 'error' });
                    if (arrayResponse.success) {
                        incrementCartItemsTotal();
                        await fetchCart();
                    }
                }
            } else {
                setSnackbar({ open: true, message: `You can only select up to ${maxScheduleDays} days`, severity: 'error' });
            }
        } else {
            const arrayResponse = await addToCartItem(pricingId, user.selectedChild!.id);
            setSnackbar({ open: true, message: arrayResponse.message, severity: arrayResponse.success ? 'success' : 'error' });
            if (arrayResponse.success) {
                incrementCartItemsTotal();
                await fetchCart();
            }
        }
    };

    const handleAddToWaitlist = (pricingId: number) => {
        setOpenWaitlistDialog({ id: pricingId, open: true });
    };

    const handleCloseWaitlistDialog = () => {
        setOpenWaitlistDialog({ id: null, open: false });
    };

    const handleConfirmWaitlist = async () => {
        if (!user || !user.selectedChild || openWaitlistDialog.id === null) return;

        const response = await addToWaitlist({
            pricingId: openWaitlistDialog.id,
            childId: user.selectedChild.id,
            userId: Number(user.uid),
        });

        setSnackbar({ open: true, message: response.message, severity: response.success ? 'success' : 'error' });
        handleCloseWaitlistDialog();
    };

    const handleOpenDialog = (pricingId: number, selectedDays: string[]) => {
        setCurrentPricingId(pricingId);
        setCurrentSelectedDays(selectedDays);
        setOpenDialog(true);
    };

    const handleCloseDialog = () => {
        setOpenDialog(false);
    };

    const handleConfirmAddToCart = async () => {
        if (!currentPricingId || !user || !user.selectedChild || !currentSelectedDays) return;

        const arrayResponse = await addToCartItem(currentPricingId, user.selectedChild.id, currentSelectedDays);
        setSnackbar({ open: true, message: arrayResponse.message, severity: arrayResponse.success ? 'success' : 'error' });
        if (arrayResponse.success) {
            incrementCartItemsTotal();
            await fetchCart();
        }
        handleCloseDialog();
    };

    const handleCloseSnackbar = () => {
        setSnackbar({ ...snackbar, open: false });
    };

    const isItemInCart = (pricingId: number, childId: number): boolean => {
        if (cartItems && cartItems.data && cartItems.data.length > 0) {
            return cartItems.data.some(item => item.item.id === pricingId && item.child.id === childId);
        }
        return false;
    };

    const isEventInCart = (eventId: number, childId: number): boolean => {
        return cartItems?.data?.some(item => 
            item.item.event.id === eventId && item.child.id === childId
        ) || false;
    };

    const handleRemoveItemFromCart = async (pricingId: number) => {
        setLoading(true);
        try {
            if (!user || !user.selectedChild) return;

            const cartItem = cartItems?.data?.find(item => item.item.id === pricingId && item.child.id === user.selectedChild!.id);
            
            if (!cartItem) {
                setSnackbar({ open: true, message: "Item not found in cart", severity: 'error' });
                setLoading(false);
                return;
            }
    
            const success = await removeItemFromCart(cartItem.id);
            if (success) {
                await fetchCart();
                setSnackbar({ open: true, message: "Item removed from cart", severity: 'success' });
            } else {
                setSnackbar({ open: true, message: "Failed to remove item from cart", severity: 'error' });
            }
        } catch (error) {
            setSnackbar({ open: true, message: "Failed to remove item from cart", severity: 'error' });
        } finally {
            setLoading(false);
        }
    };

    if (loading) {
        return <Container maxWidth="lg">
            <Grid container justifyContent="center" alignItems="center" textAlign="center" spacing={2}>
                <Grid item xs={12} sm={12} mt={5}>
                    <CircularProgress />
                </Grid>
            </Grid>    
        </Container>;
    }

    if (!event || event.status != KEY_EVENT_STATUS_ACTIVE || !eventDetails || event.isVisible == 0) {
        return <Container maxWidth="lg">
            <Grid container justifyContent="space-between" alignItems="center" spacing={2}>
                <Grid item xs={12} sm={9} mt={5}>
                    <Typography variant="h5">Error 404 - Event not found.</Typography>
                </Grid>
            </Grid>    
        </Container>;
    }

    const { className } = getEventStatus(event, user?.selectedChild?.id);

    return (
        <Container maxWidth="lg" className='event-page'>
            <EventHeader eventName={event.name} eventLocation={event.location?.name || ''} onShare={handleShare} />

            <Grid container spacing={4}>
                <Grid item xs={12} sm={12}>
                    <EventGallery images={event.images || []} />
                </Grid>
            </Grid>

            <Grid container spacing={4}>
                <Grid item xs={12} md={9}>
                    <Grid container spacing={4}>
                        <Grid item xs={12}>
                            <EventDescription event={event} />
                            <hr />
                        </Grid>
                        <Grid item xs={12} className='ptf-0'>
                            <EventKeyInformation event={event} />
                            <hr />
                        </Grid>
                        <Grid item xs={12} className='ptf-0'>
                            <EventPricingTiersBoxes
                                eventDetails={event}
                                user={user}
                                className={className}
                                isItemInCart={isItemInCart}
                                isEventInCart={isEventInCart}
                                handleAddToCartClick={handleAddToCartClick}
                                handleRemoveItemFromCart={handleRemoveItemFromCart}
                                handleAddToWaitlist={handleAddToWaitlist}
                                expandedAccordions={expandedAccordions}
                                setExpandedAccordions={setExpandedAccordions}
                            />
                        </Grid>
                    </Grid>
                </Grid>
                <Grid item xs={12} md={3}>
                    <Grid container spacing={4}>
                        <Grid item xs={12}>
                            <EventStatusPaper event={event} selectedChildId={user?.selectedChild?.id} />
                        </Grid>
                    </Grid>
                </Grid>
            </Grid>

            <CustomSnackbar
                open={snackbar.open}
                message={snackbar.message}
                severity={snackbar.severity}
                handleClose={handleCloseSnackbar}
            />
            <ConfirmDialog
                open={openDialog}
                handleClose={handleCloseDialog}
                handleConfirm={handleConfirmAddToCart}
                title="Confirm Action"
                description="You have selected fewer days than the maximum allowed. Are you sure you want to proceed?"
            />
            <ConfirmDialog
                open={openWaitlistDialog.open}
                handleClose={handleCloseWaitlistDialog}
                handleConfirm={handleConfirmWaitlist}
                title={`${event.name} waitlist`}
                description={`Please confirm you would like to be added to the ${event.name} waitlist`}
            />
        </Container>
    );
};

export default EventPage;